import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { Loading, useLogoutSync } from '@crossnokaye/ux-common';

import { AuthConfigs } from '@/config';
import { authHeaderProvider } from '@/singletons';

import { AuthContext, AuthContextValue } from './types';

export const AuthorizationBoundary = ({ children }: PropsWithChildren) => {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    logout: auth0Logout,
    user,
  } = useAuth0();

  const getAccessToken = useCallback(async () => {
    return await getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  // Set up logout synchronization across apps and tabs
  const { broadcastLogout } = useLogoutSync({
    clientId: AuthConfigs.clientId,
    isAuthenticated,
    isLoading,
    onLogoutDetected: () => {
      window.location.reload();
    },
  });

  const logout = useCallback(() => {
    // Broadcast logout event for instant tab synchronization
    broadcastLogout();
    // Perform normal logout with redirect
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout, broadcastLogout]);

  const login = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
      },
    });
  }, [loginWithRedirect]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      isAuthenticated,
      isViewOnly: false, // Login app doesn't have view-only mode
      user,
      getAccessToken: async ({ cacheMode }: { cacheMode?: 'off' | 'on' } = {}) => {
        return await getAccessTokenSilently({ cacheMode });
      },
      requestViewOnly: () => {}, // Not implemented in login app
      login,
      logout,
    }),
    [isAuthenticated, user, getAccessTokenSilently, login, logout]
  );

  useEffect(() => {
    if (isAuthenticated) {
      authHeaderProvider.set(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  // If not loaded yet, show a spinner
  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
